<template>
  <v-container class="financial-page">
    <Overview :value="overview" />
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pa-0 ma-0">
        <div class="default-table cashflow-table" style="position: relative;">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="permissions.financial.cash_flow.actions.new"
                v-on="on"
                @click.prevent="newDialog()"
                color="primary"
                class="mb-9"
                app
                fab
                bottom
                right
                fixed
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ lang.financial.cashflow.new.title }}</span>
          </v-tooltip>
          <v-row>
            <Search name="financial.cashflow">
              <Filters v-model="filters" />
            </Search>
          </v-row>
          <v-row>
            <v-col cols="12" lg="3" md="5" sm="12" xs="12" class="stats-col">
              <Info :filters="filters" @clearFilters="clearFilters" />
            </v-col>
            <v-col cols="12" lg="9" md="7" sm="12" xs="12" class="pa-0 ma-0">
              <Save
                v-if="dialog.selected"
                v-model="dialog.selected"
                :id="dialog.selected.id"
                :dialog="dialog.save"
                :key="`save_${(dialog.selected || {}).id}`"
                @closed="onCloseSave"
              />
              <ViewDialog
                v-if="dialog.selected"
                v-model="dialog.selected"
                :id="dialog.selected.id"
                :dialog="dialog.view"
                :key="`view_${(dialog.selected || {}).id}`"
                @closed="onCloseView"
                @edit="editDialog(dialog.selected)"
              />
              <DataTable
                name="financial.cashflow"
                :items="data"
                :tablehdrs="tableheaders.financial.cashflow"
                itemkey="id"
                sortby="datetime"
                groupby="date"
                sortdesc
                hideheader
              >
                <template v-slot:groupName="{ group }">
                  {{ formatDateLL(group) }}
                </template>
                <template v-slot:default="{ item }">
                  <tr
                    class="table-row cashflow-row"
                    :class="(item.type === 1 ? 'income' : 'expense')"
                  >
                    <td>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            v-text="getCat(item).icon"
                            class="ma-auto"
                            style="display: table !important;"
                            large
                          />
                        </template>
                        <span>{{ getCat(item).title }}</span>
                      </v-tooltip>
                    </td>
                    <td class="date-col">
                      <span class="datetime">
                        {{ formatDate(item.datetime, true, true) }}
                      </span>
                    </td>
                    <td class="title-col">
                      <span>{{ item.title }}</span>
                    </td>
                    <td class="value-col">
                      <v-icon
                        v-if="item.type === 1"
                        v-text="`mdi-arrow-up-bold`"
                        color="lime accent-4"
                        class="mb-1"
                        large
                      />
                      <v-icon
                        v-else
                        v-text="`mdi-arrow-down-bold`"
                        color="deep-orange accent-3"
                        large
                      />
                      <span class="text-body-1 font-weight-black">{{ formatCurrency(item.value) }}</span>
                    </td>
                    <td class="actions-col">
                      <Actions
                        :value="item"
                        @view="viewDialog(item)"
                        @edit="editDialog(item)"
                      />
                    </td>
                  </tr>
                </template>
                <template v-slot:mobile="{ item }">
                  <tr class="mobile_row table-row cashflow-row">
                    <td class="mobile_col">
                      <ul class="flex-content pl-2">
                        <li class="flex-item">ÍCONE</li>
                        <li class="flex-item" :data-label="lang.financial.cashflow.datatable.headers.datetime">
                          {{ formatDate(item.datetime) }}
                        </li>
                        <li class="flex-item">{{ item.title }}</li>
                        <li class="flex-item" :data-label="lang.financial.cashflow.datatable.headers.value">
                          {{ formatCurrency(item.value) }}
                        </li>
                        <li class="flex-item actions-row" :data-label="lang.financial.cashflow.datatable.headers.actions">
                          <Actions
                            :value="item"
                            @view="viewDialog(item)"
                            @edit="editDialog(item)"
                          />
                        </li>
                      </ul>
                    </td>
                  </tr>
                </template>
              </DataTable>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Overview from './templates/Overview'
import Search from '@/templates/global/Search'
import DataTable from '@/templates/global/DataTable'
import Info from './templates/Info'
import Filters from './templates/Filters'
import Actions from './templates/Actions'
import Save from './Save'
import ViewDialog from './View'

import defaults from '@/modules/default'
import appdata from '@/mixins/appdata'
import utils from '@/mixins/utils'
import listDefaults from '@/mixins/defaults/list_subpage'
import cashflow from './mixins/index'
import filters from '@/mixins/defaults/filters'

export default {
  components: { Overview, Search, Info, Filters, DataTable, Actions, Save, ViewDialog },
  mixins: [ appdata, utils, listDefaults, cashflow ],
  data: () => ({
    fab: false,
    default: { ...defaults.blank.financial.cashflow },
    filters: { ...filters.financial.cashflow }
  }),
  computed: {
    filtered () {
      let data = (this.get_data || {}).financial || {},
          payments = (data.payments || []).map(pay => ({type: 1, ...pay})),
          expenses = (data.expenses || []).map(exp => ({type: 2, ...exp})),
          rtrn = payments.concat(expenses) || []
      
      return {
        payments: this.dataFilter(data.payments),
        unpaid: this.dataFilter(data.unpaid),
        expenses: this.dataFilter(data.expenses),
        list: this.dataFilter(rtrn)
      }
    },
    overview () {
      let data = this.filtered || {},
          income = (data.payments || []).reduce((acc, curr) => parseFloat((acc + curr.value).toFixed(10)), 0),
          unpaid = (data.unpaid || []).reduce((acc, curr) => parseFloat((acc + (curr.total - curr.total_paid)).toFixed(10)), 0),
          expenses = (data.expenses || []).reduce((acc, curr) => parseFloat((acc + curr.value).toFixed(10)), 0),
          profit = parseFloat(income - expenses).toFixed(10)
      return { income, unpaid, expenses, profit }
    },
    data () {
      let data = (this.filtered || {}).list || []
      return data.map(el => {
        // el.date = this.formatDateLL((el.datetime || '').split(' ')[0])
        el.date = (el.datetime || '').split(' ')[0]
        return el
      })
    }
  },
  methods: {
    dataFilter (rtrn) {
      let { date, type, payment, categories } = this.filters || {}
      // DATE FILTER
      rtrn = (rtrn || []).filter((e) => {
        let created = new Date(e.datetime),
            start = new Date(`${date[0]} 00:00:00`),
            end = new Date(`${date[1]} 23:59:59`)
        return ((isNaN(start.getTime()) || start <= created) && (isNaN(end.getTime()) || end >= created))
      })
      // TYPE FILTER
      if (type > 0) rtrn = rtrn.filter(e => e.type === type)
      // PAYMENT METHOD FILTER
      if (payment.length > 0) rtrn = rtrn.filter(e => (e.type === 1 && payment.indexOf(e.method) > -1))
      // EXPENSE CATEGORIES FILTER
      if (categories.length > 0) rtrn = rtrn.filter(e => (e.type === 2 && categories.indexOf(e.category) > -1))

      return rtrn
    },
    clearFilters () {
      this.filters = { ...filters.financial.cashflow }
    },
    newDialog_ (type) {
      this.dialog.selected = Object.assign({}, this.default)
      this.dialog.selected.type = type
      this.dialog.view = false
      this.$router.push({
        name: `${this.routerPath.root}${(this.routerPath.page && this.routerPath.page !== 'default' ? ':' + this.routerPath.type : '')}:new`,
        params: {
          page: (this.routerPath.page ? this.routerPath.page_path : false)
        }
      }).catch(err => { if (err.name !== 'NavigationDuplicated') console.error(err) })
      this.dialog.save = true
    }
  }
}
</script>

<style src="./styles/index.scss" lang="scss"/>
